/**
 * HeroMockups — Visar 6 exempelinlägg (Facebook / Instagram / LinkedIn)
 * som en mjukt rullande mockup-vägg i hero på /home-2.
 *
 * Tre kolumner på desktop, en kolumn på mobil. Varannan kolumn rullar
 * uppåt/nedåt i mycket långsam takt (~40s) för att ge igenkänning utan
 * att stjäla fokus. Pausas vid prefers-reduced-motion. aria-hidden
 * eftersom innehållet är dekorativt — texten på sidan beskriver värdet.
 */

// Använder CSS @media (prefers-reduced-motion) i stället för framer-motions
// useReducedMotion-hook för att hålla framer-motion utanför initial-bundlen.
import nordvik from "@/assets/hero-mockups/nordvik-bygg.webp";
import kvarterskoket from "@/assets/hero-mockups/kvarterskoket.webp";
import klarsikt from "@/assets/hero-mockups/klarsikt.webp";
import studioForm from "@/assets/hero-mockups/studio-form.webp";
import nordmark from "@/assets/hero-mockups/nordmark.webp";
import balans from "@/assets/hero-mockups/balans-halsa.webp";

const COLS: string[][] = [
  [kvarterskoket, studioForm],
  [klarsikt, balans],
  [nordvik, nordmark],
];

export default function HeroMockups() {
  return (
    <div
      aria-hidden="true"
      className="relative w-full overflow-hidden rounded-2xl h-[380px] sm:h-[480px] md:h-[560px]"
    >
      {/* Top/bottom fade-mask för mjuk inramning */}
      <div
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(to bottom, var(--color-v3-bg-primary, #FFFAF5) 0%, transparent 12%, transparent 88%, var(--color-v3-bg-primary, #FFFAF5) 100%)",
        }}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 h-full px-2">
        {COLS.map((images, colIdx) => {
          const direction = colIdx % 2 === 0 ? "up" : "down";
          // Hide 3rd column on small screens
          const hiddenClass = colIdx === 2 ? "hidden md:block" : "";
          return (
            <div
              key={colIdx}
              className={`relative overflow-hidden ${hiddenClass}`}
            >
              <div
                className="flex flex-col gap-4 md:gap-6 hero-mockup-scroll"
                data-direction={direction}
              >
                {/* Render twice for seamless loop */}
                {[...images, ...images, ...images].map((src, i) => (
                  <img
                    key={`${colIdx}-${i}`}
                    src={src}
                    alt=""
                    width={600}
                    height={750}
                    loading={i < images.length ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={i === 0 ? "high" : "low"}
                    className="w-full h-auto rounded-xl shadow-[0_8px_24px_rgba(27,58,75,0.08)]"
                    draggable={false}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .hero-mockup-scroll[data-direction="up"]   { animation: hero-mockup-scroll-up   50s linear infinite; }
        .hero-mockup-scroll[data-direction="down"] { animation: hero-mockup-scroll-down 50s linear infinite; }
        @keyframes hero-mockup-scroll-up {
          0% { transform: translateY(0); }
          100% { transform: translateY(-33.333%); }
        }
        @keyframes hero-mockup-scroll-down {
          0% { transform: translateY(-33.333%); }
          100% { transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-mockup-scroll { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
